import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Popconfirm, Space, Input, Modal, Form, Typography, Select, message, Switch } from 'antd';
import { UserAddOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, UserSwitchOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const roleMap = {
  super_admin: { label: '超级管理员', color: 'red', level: 100 },
  admin: { label: '管理员', color: 'blue', level: 50 },
  operator: { label: '运营人员', color: 'green', level: 30 },
  auditor: { label: '审计员', color: 'orange', level: 20 },
};

function AdminUserManagePage() {
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadAdmins = async (p = 1) => {
    setLoading(true);
    try {
      const { list, total: t } = await api.get('/admin/admin-users', { params: { page: p, limit: 20 } });
      setAdmins(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAdmins(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/admin/admin-users', values);
      message.success('管理员创建成功');
      setCreateModal(false);
      form.resetFields();
      loadAdmins(page);
    } catch (e) {
      message.error(e.errorFields ? '请填写完整' : (e.error || '创建失败'));
    }
  };

  const handleEdit = (record) => {
    setSelected(record);
    editForm.setFieldsValue(record);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    const values = editForm.getFieldsValue();
    await api.put(`/admin/admin-users/${selected.id}`, values);
    message.success('更新成功');
    setEditModal(false);
    loadAdmins(page);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    await api.put(`/admin/admin-users/${id}`, { status: newStatus });
    message.success(newStatus === 1 ? '已启用' : '已禁用');
    loadAdmins(page);
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/admin-users/${id}`);
    message.success('已删除');
    loadAdmins(page);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '手机号', dataIndex: 'phone', width: 130 },
    { title: '昵称', dataIndex: 'nickname', width: 120 },
    {
      title: '角色', width: 120,
      render: (_, r) => <Tag color={roleMap[r.role]?.color}>{roleMap[r.role]?.label || r.role}</Tag>,
    },
    {
      title: '状态', width: 100,
      render: (_, r) => (
        <Switch
          checked={r.status === 1}
          onChange={() => handleToggleStatus(r.id, r.status)}
          checkedChildren="正常"
          unCheckedChildren="禁用"
        />
      ),
    },
    { title: '最后登录', dataIndex: 'last_login', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '从未登录' },
    { title: '登录次数', dataIndex: 'login_count', width: 90, align: 'center' },
    { title: '创建时间', dataIndex: 'created_at', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(r.id)} icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>管理员管理</Title>
        <Button type="primary" icon={<UserAddOutlined />} onClick={() => setCreateModal(true)} style={{ background: '#07C160' }}>新增管理员</Button>
      </div>

      <Table columns={columns} dataSource={admins} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 20, onChange: loadAdmins, showSizeChanger: false }} size="small" />

      {/* 创建 */}
      <Modal title="新增管理员" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}><Input /></Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, message: '请输入密码' }]}><Input.Password /></Form.Item>
          <Form.Item name="nickname" label="昵称"><Input /></Form.Item>
          <Form.Item name="role" label="角色" initialValue="operator" rules={[{ required: true }]}>
            <Select options={Object.entries(roleMap).map(([k, v]) => ({ value: k, label: `${v.label} (等级${v.level})` }))} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑 */}
      <Modal title={`编辑管理员 - ${selected?.nickname || ''}`} open={editModal} onOk={handleEditSave} onCancel={() => setEditModal(false)}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="nickname" label="昵称"><Input /></Form.Item>
          <Form.Item name="password" label="重置密码"><Input.Password placeholder="留空则不修改" /></Form.Item>
          <Form.Item name="role" label="角色">
            <Select options={Object.entries(roleMap).map(([k, v]) => ({ value: k, label: `${v.label} (等级${v.level})` }))} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ value: 1, label: '正常' }, { value: 0, label: '禁用' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default AdminUserManagePage;
